import * as React from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CalendarCalendar } from '@/hooks/useCalendar';

interface CalendarSelectProps {
  calendars: CalendarCalendar[];
  value: string;
  onChange: (id: string) => void;
}

/** Human label for a connected account, e.g. "Google — ian@gmail.com". */
function accountLabel(provider: CalendarCalendar['provider']): string {
  const type =
    provider?.type === 'gmail' ? 'Google'
    : provider?.type === 'outlook' ? 'Outlook'
    : (provider?.type ?? 'Account');
  const who = provider?.email || provider?.display_name || '';
  return who ? `${type} — ${who}` : type;
}

function ColorDot({ color }: { color?: string }) {
  return (
    <div
      aria-hidden="true"
      className="h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color || '#6b7280' }}
    />
  );
}

/**
 * Destination picker for a new/edited event. The local "Buildit" calendar
 * (provider_id === null) is listed first as the default; connected Google/Outlook
 * calendars are grouped under their account. Extra props (id / aria-*) are
 * forwarded to the trigger so it can be wired by a <FormControl>.
 */
export const CalendarSelect = React.forwardRef<
  HTMLButtonElement,
  CalendarSelectProps & Omit<React.ComponentPropsWithoutRef<typeof SelectTrigger>, 'value' | 'onChange'>
>(({ calendars, value, onChange, ...triggerProps }, ref) => {
  const local = calendars.filter(c => c.provider_id == null);
  const remote = calendars.filter(c => c.provider_id != null);

  // Group remote calendars by provider account (provider_id), preserving order.
  const groups: { key: string; label: string; calendars: CalendarCalendar[] }[] = [];
  const byKey = new Map<string, { key: string; label: string; calendars: CalendarCalendar[] }>();
  for (const cal of remote) {
    const key = cal.provider_id as string;
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: accountLabel(cal.provider), calendars: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.calendars.push(cal);
  }

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger ref={ref} aria-label="Calendar" {...triggerProps}>
        <SelectValue placeholder="Select a calendar" />
      </SelectTrigger>
      <SelectContent>
        {local.map(cal => (
          <SelectItem key={cal.id} value={cal.id}>
            <div className="flex items-center gap-2">
              <ColorDot color={cal.color} />
              {cal.name}
            </div>
          </SelectItem>
        ))}
        {groups.map(group => (
          <SelectGroup key={group.key}>
            <SelectLabel>{group.label}</SelectLabel>
            {group.calendars.map(cal => (
              <SelectItem key={cal.id} value={cal.id}>
                <div className="flex items-center gap-2">
                  <ColorDot color={cal.color} />
                  {cal.name}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
});

CalendarSelect.displayName = 'CalendarSelect';
